import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, UserPlusIcon } from "@heroicons/react/24/solid";
import { useGetUnassignedUsers, useAssignUserToClient } from "../../queries/admin";

export default function AssignUserModal({
  isOpen,
  onClose,
  clientId,
  clientName,
}: {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
}) {
  const { data: unassignedUsers, isLoading } = useGetUnassignedUsers();
  const { mutate: assignUser, isPending } = useAssignUserToClient();

  const handleAssign = (userId: string) => {
    assignUser(
      { userId, clientId },
      {
        onSuccess: () => {
          // If no more unassigned users, close the modal
          if (unassignedUsers && unassignedUsers.length <= 1) {
            onClose();
          }
        },
      },
    );
  };

  return (
    <Transition show={isOpen}>
      <Dialog className="relative z-50" onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="border-b border-gray-200 dark:border-gray-700 pb-3 mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                      Assign Users to {clientName}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Users without an organization
                    </p>
                  </div>
                  <XMarkIcon
                    className="w-6 h-6 cursor-pointer text-gray-400 hover:text-gray-600"
                    onClick={onClose}
                  />
                </div>

                {isLoading ? (
                  <div className="py-8 text-center text-gray-500">Loading...</div>
                ) : !unassignedUsers || unassignedUsers.length === 0 ? (
                  <div className="py-8 text-center">
                    <UserPlusIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      No unassigned users found
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {unassignedUsers.map((user: any) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {user.email}
                          </p>
                        </div>
                        <button
                          onClick={() => handleAssign(user.id)}
                          disabled={isPending}
                          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
                        >
                          <UserPlusIcon className="h-3.5 w-3.5" />
                          Assign
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={onClose}
                    className="rounded-md px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
